import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ConfigService } from './config.service';
import { core, getUnitInfo, logger, ToastService } from '@icc/shared/helpers/b2b';
import { UnitsService } from './units.service';
import { TranslateService } from '@ngx-translate/core';
import { PricesDataSharedService } from './prices-data-shared.service';

@Injectable({
    providedIn: 'root',
})
export class PricesDataService extends PricesDataSharedService {
    productPrices: Record<number, Record<number, any>> = {};
    configuratorPrices: Record<number, any[]> = {};
    pricesRevisions: Record<number, number> = {};
    contractorPrices: Record<number, Record<number, Record<number, any>>> = {};
    productUnits: Record<number, any> = {};

    loadedPrices$: Record<number, Promise<void>> = {};
    loadedPrices: Record<number, boolean> = {};
    private resolveLoadedPrices: Record<number, () => void> = {};
    private rejectLoadedPrices: Record<number, (reason?: any) => void> = {};

    loadedContractorPrices$: Record<number, Record<number, Promise<void>>> = {};
    loadedContractorPrices: Record<number, Record<number, boolean>> = {};
    private resolveLoadedContractorPrices: Record<number, Record<number, () => void>> = {};
    private rejectLoadedContractorPrices: Record<number, Record<number, (reason?: any) => void>> = {};

    priceRasters: {
        [priceVersionId: number]: {
            [configuratorId: string]: any[];
        }
    } = {};
    priceRastersRevision: {
        [priceVersionId: number]: {
            [configuratorId: string]: number;
        }
    } = {};
    loadedPriceRasters$: {
        [priceVersionId: number]: {
            [configuratorId: string]: Promise<void>;
        }
    } = {};
    loadedPriceRasters: {
        [priceVersionId: number]: {
            [configuratorId: string]: boolean;
        }
    } = {};
    private resolveLoadedPriceRasters: {
        [priceVersionId: number]: {
            [configuratorId: string]: () => void;
        }
    } = {};
    private rejectLoadedPriceRasters: {
        [priceVersionId: number]: {
            [configuratorId: string]: (reason?: any) => void;
        }
    } = {};

    loaded$ = new BehaviorSubject<{
        [priceVersionId: number]: {
            [configuratorId: string]: boolean;
        }
    }>({});
    loaded: {
        [priceVersionId: number]: {
            [configuratorId: string]: boolean;
        }
    } = {};
    loading: Record<string, boolean> = {};

    private isInConfigurator = false;
    private hasDeferredClear = false;
    private pricesLoadErrorNotificationShown = false;

    constructor(
        private http: HttpClient,
        private configService: ConfigService,
        private unitsService: UnitsService,
        private translateService: TranslateService,
        private toastService: ToastService,
    ) {
        super();
        (window as any).iccPricesDataService = this;
    }

    private async loadWithRetry<T>(loaderName: string, loader: () => Promise<T>): Promise<T> {
        const maxAttempts = 2;
        let lastError: any;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                return await loader();
            } catch (error) {
                lastError = error;
                logger.error(`[SYNC] Failed to load ${loaderName}, attempt ${attempt}/${maxAttempts}`, error);
                if (attempt < maxAttempts) {
                    await this.wait(500);
                }
            }
        }

        this.showPricesLoadErrorNotification();
        throw lastError;
    }

    private wait(time: number) {
        return new Promise((resolve) => setTimeout(resolve, time));
    }

    private startBackgroundLoading(loading: Promise<void>) {
        void loading.catch((error) => logger.error(error));
    }

    private showPricesLoadErrorNotification() {
        if (this.pricesLoadErrorNotificationShown) {
            return;
        }

        this.pricesLoadErrorNotificationShown = true;
        this.toastService.add({
            title: this.translateService.instant('INTERFACE|Nie udało się załadować cen'),
            body: this.translateService.instant(
                'INTERFACE|Nie udało się załadować danych cenowych. Odśwież stronę w przeglądarce i spróbuj ponownie.'
            ),
            sticky: true,
            severity: 'error',
        });
    }

    private setEmptyPricesForVersion(id: number) {
        this.productPrices[id] = this.productPrices[id] || {};
        this.configuratorPrices[id] = this.configuratorPrices[id] || [];
        this.pricesRevisions[id] = this.pricesRevisions[id] || 0;
        this.productUnits = this.productUnits || {};
        this.loadedPrices[id] = true;

        if (this.resolveLoadedPrices[id]) {
            this.resolveLoadedPrices[id]();
        }
    }

    private setEmptyRastersForVersion(id: number, configuratorId: string) {
        if (!this.priceRasters[id]) {
            this.priceRasters[id] = {};
        }
        if (!this.priceRastersRevision[id]) {
            this.priceRastersRevision[id] = {};
        }
        if (!this.loadedPriceRasters[id]) {
            this.loadedPriceRasters[id] = {};
        }

        this.priceRasters[id][configuratorId] = this.priceRasters[id][configuratorId] || [];
        this.priceRastersRevision[id][configuratorId] = this.priceRastersRevision[id][configuratorId] || 0;
        this.loadedPriceRasters[id][configuratorId] = true;

        if (this.resolveLoadedPriceRasters[id]?.[configuratorId]) {
            this.resolveLoadedPriceRasters[id][configuratorId]();
        }
    }

    /**
     * Ustawia flagę wskazującą, czy konfigurator jest obecnie otwarty
     */
    setConfiguratorState(isOpen: boolean) {
        logger.log('[SYNC] Configurator state changed:', isOpen ? 'opened' : 'closed');
        this.isInConfigurator = isOpen;

        // Jeśli konfigurator został zamknięty i było odroczone czyszczenie cache
        if (!isOpen && this.hasDeferredClear) {
            logger.log('[SYNC] Executing deferred prices cache clear');
            this.hasDeferredClear = false;
            this.clearPricesCache();
        }
    }

    public async loadPricesForPriceVersion(id: number) {
        this.loading[`prices-${id}`] = true;
        try {
            if (!this.loadedPrices$[id]) {
                this.loadedPrices$[id] = new Promise((resolve, reject) => {
                    this.resolveLoadedPrices[id] = resolve;
                    this.rejectLoadedPrices[id] = reject;
                });
                void this.loadedPrices$[id].catch(() => undefined);
            }
            if (id <= 0) {
                this.setEmptyPricesForVersion(id);
                return;
            }
            if (!this.productPrices[id] || !this.configuratorPrices[id]) {
                const result = await this.loadWithRetry('prices', () =>
                    this.http
                        .get<any>(
                            `${window.location.origin}/api/b2b/prices/${id}?lang=${this.configService.config.languageCode}&marketId=${this.configService.user?.user?.ActiveMarket}`
                        )
                        .toPromise()
                ).catch((error) => {
                    this.rejectLoadedPrices[id]?.(error);
                    delete this.loadedPrices$[id];
                    delete this.resolveLoadedPrices[id];
                    delete this.rejectLoadedPrices[id];
                    throw error;
                });
                this.productPrices[id] = result?.productPrices || {};
                this.configuratorPrices[id] = result?.configuratorPrices || [];
                this.pricesRevisions[id] = result?.revision || 0;
                this.productUnits = result?.productUnits || {};
                this.unitsService.setUnits(this.productUnits);

                this.loadedPrices[id] = true;
                this.resolveLoadedPrices[id]();
            }
        } finally {
            this.loading[`prices-${id}`] = false;
        }
    }

    public async loadPricesForContractor(contractorCompanyId: number, priceVersionId: number) {
        this.loading[`prices-contractor-${priceVersionId}-${contractorCompanyId}`] = true;
        try {
            if (!this.loadedContractorPrices$[priceVersionId]) {
                this.loadedContractorPrices$[priceVersionId] = {};
            }
            if (!this.loadedContractorPrices$[priceVersionId][contractorCompanyId]) {
                this.loadedContractorPrices$[priceVersionId][contractorCompanyId] = new Promise((resolve, reject) => {
                    if (!this.resolveLoadedContractorPrices[priceVersionId]) {
                        this.resolveLoadedContractorPrices[priceVersionId] = {};
                    }
                    if (!this.rejectLoadedContractorPrices[priceVersionId]) {
                        this.rejectLoadedContractorPrices[priceVersionId] = {};
                    }
                    this.resolveLoadedContractorPrices[priceVersionId][contractorCompanyId] = resolve;
                    this.rejectLoadedContractorPrices[priceVersionId][contractorCompanyId] = reject;
                });
                void this.loadedContractorPrices$[priceVersionId][contractorCompanyId].catch(
                    () => undefined
                );
            }
            if (!this.contractorPrices[priceVersionId]?.[contractorCompanyId]) {
                const result = await this.loadWithRetry('contractor prices', () =>
                    this.http
                        .get<any>(
                            `${window.location.origin}/api/b2b/prices-contractor/${contractorCompanyId}/${priceVersionId}`
                        )
                        .toPromise()
                ).catch((error) => {
                    this.rejectLoadedContractorPrices[priceVersionId]?.[contractorCompanyId]?.(error);
                    delete this.loadedContractorPrices$[priceVersionId]?.[contractorCompanyId];
                    delete this.resolveLoadedContractorPrices[priceVersionId]?.[contractorCompanyId];
                    delete this.rejectLoadedContractorPrices[priceVersionId]?.[contractorCompanyId];
                    throw error;
                });
                if (!this.contractorPrices[priceVersionId]) {
                    this.contractorPrices[priceVersionId] = {};
                }
                this.contractorPrices[priceVersionId][contractorCompanyId] = result?.contractorPrices || {};

                if (!this.loadedContractorPrices[priceVersionId]) {
                    this.loadedContractorPrices[priceVersionId] = {};
                }
                this.loadedContractorPrices[priceVersionId][contractorCompanyId] = true;
                this.resolveLoadedContractorPrices[priceVersionId][contractorCompanyId]();
            }
        } finally {
            this.loading[`prices-contractor-${priceVersionId}-${contractorCompanyId}`] = false;
        }
    }

    public async loadPriceRastersForPriceVersion(id: number, configuratorId: string) {
        this.loading[`rasters-${id}-${configuratorId}`] = true;
        try {
            if (!this.loadedPriceRasters$[id]?.[configuratorId]) {
                if (!this.loadedPriceRasters$[id]) {
                    this.loadedPriceRasters$[id] = {};
                }
                this.loadedPriceRasters$[id][configuratorId] = new Promise((resolve, reject) => {
                    if (!this.resolveLoadedPriceRasters[id]) {
                        this.resolveLoadedPriceRasters[id] = {};
                    }
                    if (!this.rejectLoadedPriceRasters[id]) {
                        this.rejectLoadedPriceRasters[id] = {};
                    }
                    this.resolveLoadedPriceRasters[id][configuratorId] = resolve;
                    this.rejectLoadedPriceRasters[id][configuratorId] = reject;
                });
                void this.loadedPriceRasters$[id][configuratorId].catch(() => undefined);
            }
            if (id <= 0) {
                this.setEmptyRastersForVersion(id, configuratorId);
                return;
            }
            if (!this.priceRasters[id]?.[configuratorId]) {
                const result = await this.loadWithRetry('price rasters', () =>
                    this.downloadPriceRastersStream(id, configuratorId)
                ).catch((error) => {
                    this.rejectLoadedPriceRasters[id]?.[configuratorId]?.(error);
                    delete this.loadedPriceRasters$[id]?.[configuratorId];
                    delete this.resolveLoadedPriceRasters[id]?.[configuratorId];
                    delete this.rejectLoadedPriceRasters[id]?.[configuratorId];
                    throw error;
                });
                if (!this.priceRasters[id]) {
                    this.priceRasters[id] = {};
                }
                if (!this.priceRastersRevision[id]) {
                    this.priceRastersRevision[id] = {};
                }
                this.priceRasters[id][configuratorId] = [];
                result.forEach((priceRaster) => {
                    priceRaster.configurators?.forEach((configurator: string) => {
                        if (!this.priceRastersRevision[id][configurator]) {
                            this.priceRastersRevision[id][configurator] = 0;
                        }
                        this.priceRastersRevision[id][configurator] = priceRaster.revision || 0;
                        if (!this.priceRasters[id][configurator]) {
                            this.priceRasters[id][configurator] = [];
                        }
                        this.priceRasters[id][configurator].push(
                            priceRaster
                        );
                    });
                });
                if (!this.loadedPriceRasters[id]) {
                    this.loadedPriceRasters[id] = {};
                }
                this.loadedPriceRasters[id][configuratorId] = true;
                this.resolveLoadedPriceRasters[id][configuratorId]();
            }
        } finally {
            this.loading[`rasters-${id}-${configuratorId}`] = false;
        }
    }

    private loadPricesForPriceVersionIfNeeded(priceVersion: number) {
        if (!this.loading[`prices-${priceVersion}`]) {
            this.startBackgroundLoading(this.loadPricesForPriceVersion(priceVersion));
        }
    }

    private loadPriceRastersForPriceVersionIfNeeded(priceVersion: number, configuratorId: string) {
        if (!this.loading[`rasters-${priceVersion}-${configuratorId}`]) {
            this.startBackgroundLoading(this.loadPriceRastersForPriceVersion(priceVersion, configuratorId));
        }
    }

    private loadPricesForContractorIfNeeded(contractorCompanyId: number, priceVersionId: number) {
        if (!this.loading[`prices-contractor-${priceVersionId}-${contractorCompanyId}`]) {
            this.startBackgroundLoading(
                this.loadPricesForContractor(contractorCompanyId, priceVersionId)
            );
        }
    }

    async getProductPricesForPriceVersion(priceVersion: number, contractorCompanyId?: number | null) {
        if (priceVersion <= 0) {
            return {};
        }
        this.loadPricesForPriceVersionIfNeeded(priceVersion);
        await this.loadedPrices$[priceVersion];
        if (contractorCompanyId) {
            this.loadPricesForContractorIfNeeded(contractorCompanyId, priceVersion);
            await this.loadedContractorPrices$[priceVersion][contractorCompanyId];
            const productPrices = core.copy(this.productPrices[priceVersion]);
            for (const productId in productPrices) {
                if (this.contractorPrices[priceVersion]?.[contractorCompanyId]?.[productPrices[productId].id]) {
                    productPrices[productId].value = this.contractorPrices[priceVersion][contractorCompanyId][productPrices[productId].id];
                }
                if (productPrices[productId].priceUnits?.length) {
                    for (const priceUnit of productPrices[productId].priceUnits) {
                        if (this.contractorPrices[priceVersion]?.[contractorCompanyId]?.[priceUnit.priceId]) {
                            priceUnit.value = this.contractorPrices[priceVersion][contractorCompanyId][priceUnit.priceId];
                        }
                    }
                }
                productPrices[productId].revision = this.pricesRevisions[priceVersion] || 0;
            }
            return productPrices;

        }
        return this.productPrices[priceVersion];
    }

    async getConfiguratorPricesForPriceVersion(priceVersion: number, contractorCompanyId?: number | null) {
        if (priceVersion <= 0) {
            return [];
        }
        this.loadPricesForPriceVersionIfNeeded(priceVersion);
        await this.loadedPrices$[priceVersion];
        if (contractorCompanyId) {
            this.loadPricesForContractorIfNeeded(contractorCompanyId, priceVersion);
            await this.loadedContractorPrices$[priceVersion][contractorCompanyId];
        }
        return this.getConfiguratorPricesForContractor(priceVersion, contractorCompanyId);
    }

    async getPriceRastersForPriceVersion(priceVersion: number, configuratorId: string) {
        if (priceVersion <= 0) {
            return {};
        }
        this.loadPriceRastersForPriceVersionIfNeeded(priceVersion, configuratorId);
        await this.loadedPriceRasters$[priceVersion][configuratorId];
        return this.priceRasters[priceVersion];
    }

    getConfiguratorPricesForPriceVersionSync(priceVersion: number, contractorCompanyId?: number | null) {
        return this.getConfiguratorPricesForContractor(priceVersion, contractorCompanyId);
    }

    getPriceRastersForPriceVersionSync(priceVersion: number) {
        return this.priceRasters[priceVersion];
    }


    async loadPricesForConfiguratorAndPriceVersion(priceVersion: number, configuratorId: string) {
        if (priceVersion <= 0) {
            this.setEmptyPricesForVersion(priceVersion);
            this.setEmptyRastersForVersion(priceVersion, configuratorId);
            if (!this.loaded[priceVersion]) {
                this.loaded[priceVersion] = {};
            }
            this.loaded[priceVersion][configuratorId] = true;
            this.loaded$.next(this.loaded);
            return;
        }
        this.loadPricesForPriceVersionIfNeeded(priceVersion);
        const loadedPrices = this.loadedPrices$[priceVersion];
        this.loadPriceRastersForPriceVersionIfNeeded(priceVersion, configuratorId);
        const loadedPriceRasters = this.loadedPriceRasters$[priceVersion]?.[configuratorId];
        await loadedPrices;
        await loadedPriceRasters;
        if (!this.loaded[priceVersion]) {
            this.loaded[priceVersion] = {};
        }
        this.loaded[priceVersion][configuratorId] = this.loadedPriceRasters[priceVersion][configuratorId] && this.loadedPrices[priceVersion];
        this.loaded$.next(this.loaded);
    }

    getConfiguratorPricesForContractor(priceVersion: number, contractorCompanyId?: number | null) {
        if (!contractorCompanyId || !this.contractorPrices[priceVersion]?.[contractorCompanyId]) {
            return this.configuratorPrices[priceVersion];
        }
        const configuratorPrices = core.copy(this.configuratorPrices[priceVersion]);
        for (const price of configuratorPrices) {
            if (this.contractorPrices[priceVersion]?.[contractorCompanyId][price.id]) {
                price.value = this.contractorPrices[priceVersion][contractorCompanyId][price.id];
            }

            if (price.priceUnits?.length) {
                for (const priceUnit of price.priceUnits) {
                    if (this.contractorPrices[priceVersion]?.[contractorCompanyId]?.[priceUnit.priceId]) {
                        priceUnit.value = this.contractorPrices[priceVersion][contractorCompanyId][priceUnit.priceId];
                    }
                }
            }
            price.revision = this.pricesRevisions[priceVersion] || 0;
        }
        return configuratorPrices;
    }

    /**
     * Downloads price rasters stream containing ND-JSON data using fetch and Stream API
     */
    async downloadPriceRastersStream(priceVersionId: number, configuratorId: string) {
        const response = await fetch(`${window.location.origin}/api/b2b/price-rasters/${priceVersionId}/${configuratorId}`);
        if (!response.ok || !response.body) {
            throw new Error(`Price rasters request failed with status ${response.status}`);
        }
        const reader = response.body
            .pipeThrough(new TextDecoderStream())
            // split the stream into lines
            .pipeThrough((() => {
                let buffer = '';
                return new TransformStream({
                    transform(chunk, controller) {
                        buffer += chunk;
                        const parts = buffer.split('\n');
                        parts.slice(0, -1).forEach(part => controller.enqueue(part));
                        buffer = parts[parts.length - 1];
                    },
                    flush(controller) {
                        if (buffer) controller.enqueue(buffer);
                    }
                });
            })())
            .pipeThrough(new TransformStream({
                transform(chunk, controller) {
                    controller.enqueue(JSON.parse(chunk));
                }
            }))
            .getReader();

        const result = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            result.push(value);
        }

        return result;
    }

    getPricesRevisionsForPriceVersionSync(
        priceVersion: number,
        configuratorId?: string | null,
    ): { prices: number; priceRasters: number } {
        if (this.pricesRevisions[priceVersion] === undefined) {
            this.pricesRevisions[priceVersion] = 0;
        }
        if (this.priceRastersRevision[priceVersion]?.[configuratorId] === undefined) {
            this.priceRastersRevision[priceVersion] = this.priceRastersRevision[priceVersion] || {};
            this.priceRastersRevision[priceVersion][configuratorId] = 0;
        }
        return {
            prices: this.pricesRevisions[priceVersion],
            priceRasters: this.priceRastersRevision[priceVersion][configuratorId] || 0,
        };
    }

    getProductUnit(unitId: number) {
        return this.productUnits[unitId];
    }

    getProductUnits() {
        return this.productUnits;
    }

    getUnitInfo(unitId: number, unitCode: string) {
        if (unitId) {
            return this.productUnits[unitId];
        }
        return getUnitInfo(unitCode);
    }

    /**
     * Czyści cache cenników i cenników rastrowych po otrzymaniu sygnału z backendu
     * o zmianie cen, modyfikatorów lub cenników rastrowych
     */
    clearPricesCache() {
        // Jeśli konfigurator jest otwarty, odrocz czyszczenie cache
        if (this.isInConfigurator) {
            logger.log('[SYNC] Deferring prices cache clear - configurator is open');
            this.hasDeferredClear = true;
            return;
        }

        logger.log('[SYNC] Clearing prices cache');

        // Wyczyść cache cen produktów
        this.productPrices = {};
        this.configuratorPrices = {};
        this.pricesRevisions = {};
        this.contractorPrices = {};

        // Wyczyść cache cenników rastrowych
        this.priceRasters = {};
        this.priceRastersRevision = {};

        // Wyczyść flagi załadowania
        this.loading = {};
        this.loadedPrices = {};
        this.loadedContractorPrices = {};
        this.loadedPriceRasters = {};
        this.loaded = {};
        this.loaded$.next({});

        // zresetuj promise, resolve i obserwable
        this.loadedPrices$ = {};
        this.resolveLoadedPrices = {};
        this.loadedContractorPrices$ = {};
        this.resolveLoadedContractorPrices = {};
        this.rejectLoadedContractorPrices = {};
        this.loadedPriceRasters$ = {};
        this.resolveLoadedPriceRasters = {};
        this.rejectLoadedPriceRasters = {};
        this.rejectLoadedPrices = {};

        logger.log('[SYNC] Prices cache cleared successfully');
    }
}
